import { handleResponseOld, handleResponseWithNon200Errors, makeGet, makePost, makePut } from './helpers';
import { Address } from '@liveauctioneers/caterwaul-components/types/Address';
import { paymentMethods } from '@/enums';
import paymentProviders from '@/enums/paymentProviders';
import { StripeBillingAddress } from '@/types/StripeBillingAddress';

/**
 * @category Mainhost API
 * @see postAddStripeCard
 */
type PostAddStripeCardParams = {
    authToken: string;
    bidderId: number;
    cardToken: string;
    deployment: string;
};
/**
 * postAddStripeCard - posts payment data to mainhost's Bidder Stripe Customer API receiver
 * @category Mainhost API
 * @function postAddStripeCard
 * @param {PostStripePaymentParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/api/bidders/${bidderId}/stripe/customer
 * @see https://github.com/LIVEauctioneers/mainhost-prod/blob/master/httpdocs/api/bidders.php
 */
export const postAddStripeCard = ({ authToken, bidderId, cardToken, deployment }: PostAddStripeCardParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<MAINHOST-API>api/bidders/${bidderId}/stripe/customer`,
        });
        request.send({ card_token: cardToken });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Payments API
 * @see postAddLACard
 */
type PostAddLACardParams = {
    authToken: string;
    bidderId: number;
    billingAddress: Address;
    cardNonce: string;
    cardholderName: string;
    deployment: string;
    houseId: number;
    providerId: number;
};
/**
 * postAddLACard - adds a credit card to Stripe's card-on-file for bidder
 * @category Payments API
 * @function postAddLACard
 * @param {PostAddLACardParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/cards/
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/72dull/methods/POST
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/payment-redstripe/1.0.0#/default/post_cards__bidderId_
 */
export const postAddLACard = ({
    authToken,
    bidderId,
    billingAddress,
    cardholderName,
    cardNonce,
    deployment,
    houseId,
    providerId,
}: PostAddLACardParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<PAYMENT-API>cards/${bidderId}`,
        });
        request.send({
            billingAddress,
            cardholderName,
            cardNonce,
            defaultCard: true,
            houseId,
            providerId,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Payments API
 * @see postUpdateLACard
 */
type PostUpdateLACardParams = {
    authToken: string;
    bidderId: number;
    billingAddress: Address;
    cardId: number;
    defaultCard: boolean;
    deployment: string;
    enabled: boolean;
};
/**
 * postUpdateLACard - updates an existing bidder's card-on-file in Stripe
 * @category Payments API
 * @function postUpdateLACard
 * @param {PostUpdateLACardParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/bidder/${bidderId}/card
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/72dull/methods/POST
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/payment-redstripe/1.0.0#/default/post_bidder__bidderId__card__cardId_
 */
export const postUpdateLACard = ({
    authToken,
    bidderId,
    billingAddress,
    cardId,
    defaultCard,
    deployment,
    enabled,
}: PostUpdateLACardParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<PAYMENT-API>bidder/${bidderId}/card/${cardId}`,
        });
        request.send({
            billingAddress,
            defaultCard,
            enabled,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Mainhost API
 * @see postStripePayment
 */
type PostStripePaymentParams = {
    address: StripeBillingAddress;
    amount: number;
    authToken: string;
    bidderId: number;
    cardToken: any;
    catalogId: number;
    currency: string;
    deployment: string;
    riskifiedSessionId: string;
    signifydSessionId: string;
};
/**
 * postStripePayment - posts payment data to mainhost's Stripe Payments API receiver
 * @category Mainhost API
 * @function postStripePayment
 * @param {PostStripePaymentParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/api/bidders/${bidderId}/wonitems/catalogs/${catalogId}/payments
 * @see https://github.com/LIVEauctioneers/mainhost-prod/blob/master/httpdocs/api/bidders.php
 */
export const postStripePayment = ({
    address: shipping_address,
    amount,
    authToken,
    bidderId,
    cardToken: card_token,
    catalogId,
    currency,
    deployment,
    riskifiedSessionId,
    signifydSessionId,
}: PostStripePaymentParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<MAINHOST-API>api/bidders/${bidderId}/wonitems/catalogs/${catalogId}/payments`,
        });
        request.send({ amount, card_token, currency, riskifiedSessionId, shipping_address, signifydSessionId });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });
type PaymentMethods = typeof paymentMethods;
type PaymentMethodKeys = keyof PaymentMethods;
type PaymentMethodValues = PaymentMethods[PaymentMethodKeys];

/**
 * @category Mainhost API
 * @see postLAPayment
 */
type PostLAPaymentParams = {
    address: Address;
    authToken: string;
    bidderId: number;
    deployment: string;
    invoiceId: string;
    paymentMethod: PaymentMethodValues;
    paymentToken?: string;
    riskifiedSessionId?: string;
    signifydSessionId?: string;
};
/**
 * postLAPayment - posts payment data to mainhost's LA Payments / Payrix API receiver
 * @category Mainhost API
 * @function postLAPayment
 * @param {PostLAPaymentParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/api/bidders/${bidderId}/checkout/${invoiceId}
 * @see https://github.com/LIVEauctioneers/mainhost-prod/blob/master/httpdocs/api/bidders.php
 */
export const postLAPayment = ({
    address: shippingAddress,
    authToken,
    bidderId,
    deployment,
    invoiceId,
    paymentMethod,
    paymentToken,
    riskifiedSessionId,
    signifydSessionId,
}: PostLAPaymentParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<MAINHOST-API>api/bidders/${bidderId}/checkout/${invoiceId}`,
        });
        request.send({
            paymentToken,
            paymentType: paymentMethod,
            riskifiedSessionId,
            shippingAddress: {
                // @ts-ignore
                addressLine1: shippingAddress.address1 || shippingAddress.address,
                addressLine2: shippingAddress.address2,
                city: shippingAddress.city,
                country: shippingAddress.country,
                phoneNumber: shippingAddress.mobileNumber,
                postalCode: shippingAddress.zip,
                state: shippingAddress.state,
            },
            signifydSessionId,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Mainhost API
 * @see putAddStripeCard
 */
type PutAddStripeCardParams = {
    authToken: string;
    bidderId: number;
    cardToken: string;
    deployment: string;
};
/**
 * putAddStripeCard - puts changed card payment data data to mainhost's Stripe customer update API receiver
 * @category Mainhost API
 * @function putAddStripeCard
 * @param {PutAddStripeCardParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/api/bidders/${bidderId}/stripe/customer
 * @see https://github.com/LIVEauctioneers/mainhost-prod/blob/master/httpdocs/api/bidders.php
 */
export const putAddStripeCard = ({ authToken, bidderId, cardToken, deployment }: PutAddStripeCardParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePut({
            authToken,
            deployment,
            path: `<MAINHOST-API>api/bidders/${bidderId}/stripe/customer`,
        });
        request.send({ card_token: cardToken });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Payments API
 * @see getLAPaymentCards
 */
type GetLAPaymentCardsParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
};

type GetLAPaymentCardsResponse = {
    error: boolean;
    payload: [
        {
            address: Address;
            brand: string;
            customerId: string;
            defaultCard: boolean;
            id: number;
            last4: string;
            month: string;
            name: string;
            providerId: number;
            token: string;
            year: string;
        }
    ];
};
/**
 * getLAPaymentCards - gets a bidder's credit card on file
 * @category Payments API
 * @function getLAPaymentCards
 * @param {GetLAPaymentCardsParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/cards/
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/72dull/methods/GET
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/payment-redstripe/1.0.0#/default/get_cards__bidderId_
 */
export const getLAPaymentCards = ({ authToken, bidderId, deployment }: GetLAPaymentCardsParams) =>
    new Promise<GetLAPaymentCardsResponse>((resolve, reject) => {
        const request = makeGet({
            authToken,
            deployment,
            path: `<PAYMENT-API>cards/${bidderId}`,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Payments API
 * @see fetchBankAccounts
 */
type FetchBankAccountsParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
};
/**
 * fetchBankAccounts - adds a credit card to Stripe's card-on-file for bidder
 * @category Payments API
 * @function fetchBankAccounts
 * @param {FetchBankAccountsParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/bidder/${bidderId}/bankaccounts
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/h4jba4/methods/GET
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/payment-redstripe/1.0.0#/default/get_bidder__bidderId__bankaccounts
 */
export const fetchBankAccounts = ({ authToken, bidderId, deployment }: FetchBankAccountsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            authToken,
            deployment,
            path: `<PAYMENT-API>bidder/${bidderId}/bankaccounts`,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Payments API
 * @see fetchBankAccounts
 */
type PostAddBankAccountParams = {
    accountNumber: string;
    authToken: string;
    bankAccountName: string;
    bidderId: number;
    deployment: string;
    firstName: string;
    lastName: string;
    providerId: number;
    routingNumber: string;
};
/**
 * postAddBankAccount - adds new bidder ACH bank account
 * @category Payments API
 * @function postAddBankAccount
 * @param {PostAddBankAccountParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/bidder/${bidderId}/bankaccounts
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/h4jba4/methods/POST
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/payment-redstripe/1.0.0#/default/post_bidder__bidderId__bankaccounts
 */
export const postAddBankAccount = ({
    accountNumber,
    authToken,
    bankAccountName,
    bidderId,
    deployment,
    firstName,
    lastName,
    providerId,
    routingNumber,
}: PostAddBankAccountParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<PAYMENT-API>bidder/${bidderId}/bankaccounts`,
        });
        request.send({
            accountNumber,
            bankAccountName,
            defaultAccount: true,
            firstName,
            lastName,
            providerId,
            routingNumber,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Payments API
 * @see getProcessingFees
 */
type GetProcessingFeesParams = {
    authToken: string;
    balanceDue: number;
    catalogId: number;
    deployment: string;
};
/**
 * getProcessingFees - returns processing fees for a catalog
 * @category Payments API
 * @function getProcessingFees
 * @param {GetProcessingFees}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/catalog/${catalogId}/fee
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/ocmhwc/methods/GET
 */
export const getProcessingFees = ({ authToken, balanceDue, catalogId, deployment }: GetProcessingFeesParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<PAYMENT-API>catalog/${catalogId}/fee`,
        });
        request.send({ balanceDue });
        request.end((err, response) => {
            return handleResponseWithNon200Errors({ err, reject, resolve, response });
        });
    });

/**
 * @category Payments API
 * @see deleteBankAccount
 */
type DeleteBankAccountParams = {
    authToken: string;
    bankAccountId: number;
    bidderId: number;
    deployment: string;
};
/**
 * deleteBankAccount - deletes a bank account record by backAccountId and bidderId
 * @category Payments API
 * @function deleteBankAccount
 * @param {DeleteBankAccountParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/bidder/${bidderId}/bankaccounts/${bankAccountId}
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/g3y49f/methods/POST
 */
export const deleteBankAccount = ({ authToken, bankAccountId, bidderId, deployment }: DeleteBankAccountParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<PAYMENT-API>bidder/${bidderId}/bankaccounts/${bankAccountId}`,
        });
        request.send();
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Payments API
 * @see getBidderCardStatus
 */
type GetBidderCardStatusParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
};

/**
 * @category Payments API
 * @see getBidderCardStatus
 */
type GetBidderCardStatusResponse = {
    error: boolean;
    payload: {
        bidderId: number;
        cardId: number;
        isValid: boolean;
        providerId: number;
        updatedDate: string;
    };
};

/**
 * getBidderCardStatus - gets the current Bidder card-on-file validity status
 * @category Payments API
 * @function getBidderCardStatus
 * @param {GetBidderCardStatusParams}
 * @returns {GetBidderCardStatusResponse}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/bidder/${bidderId}/card/status
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/cdwk5j/methods/GET
 */
export const getBidderCardStatus = ({ authToken, bidderId, deployment }: GetBidderCardStatusParams) =>
    new Promise<GetBidderCardStatusResponse>((resolve, reject) => {
        const request = makeGet({
            authToken,
            deployment,
            path: `<PAYMENT-API>cards/${bidderId}/status`,
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Payments API
 * @see fetchPlaidTemporaryToken
 */
export type FetchPlaidTemporaryTokenParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
};

export type FetchPlaidTemporaryTokenResponse = {
    error: boolean;
    payload: string;
};

/**
 * fetchPlaidTemporaryToken - gets a plaid temporary token for a bidder
 * @category Payments API
 * @function fetchPlaidTemporaryToken
 * @param {FetchPlaidTemporaryTokenParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/plaid/createlinktoken
 */
export const fetchPlaidTemporaryToken = ({
    authToken,
    bidderId,
    deployment,
}: FetchPlaidTemporaryTokenParams): Promise<FetchPlaidTemporaryTokenResponse> =>
    new Promise<FetchPlaidTemporaryTokenResponse>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<PAYMENT-API>plaid/createlinktoken`,
        });
        request.send({
            bidderId,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Payments API
 * @see SetPlaidPublicToken
 */
export type SetPlaidPublicTokenParams = {
    accountID: string;
    authToken: string;
    bidderId: number;
    deployment: string;
    firstName: string;
    lastName: string;
    token: string;
};

export type SetPlaidPublicTokenResponse = {
    error: boolean;
    payload: {
        accountNumberSuffix: string;
        accountType: string;
        bankAccountId: number;
        bidderId: number;
        creditable: boolean;
        customerId: string;
        debitable: boolean;
        defaultAccount: boolean;
        enabled: boolean;
        firstName: string;
        holderName: string;
        houseId: number;
        lastName: string;
        nickName: string;
        providerBankAccountId: string;
        providerId: number;
        routingNumber: string;
        status: string;
        token: string;
    };
};

/**
 * setPlaidPublicToken - sets a public token for a bidder
 * @category Payments API
 * @function setPlaidPublicToken
 * @param {setPlaidPublicTokenParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/bidder/${bidderId}/bankaccounts/verifiedbyplaid
 */
export const setPlaidPublicToken = ({
    accountID,
    authToken,
    bidderId,
    deployment,
    firstName,
    lastName,
    token,
}: SetPlaidPublicTokenParams): Promise<SetPlaidPublicTokenResponse> =>
    new Promise<SetPlaidPublicTokenResponse>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<PAYMENT-API>bidder/${bidderId}/bankaccounts/verifiedbyplaid`,
        });
        request.send({
            accountID,
            defaultAccount: true,
            firstName,
            lastName,
            providerId: paymentProviders.PAYRIX,
            token,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });
